import styled from 'styled-components';
import { breakpoints, theme as appTheme } from 'theme';

const getAvatarColor = (seed: string, theme: typeof appTheme) => {
  const palette = [
    theme.color.brightTurquoise,
    theme.color.koromiko,
    theme.color.activeBlue,
    theme.color.BlueBayoux,
    theme.color.skobeloffGreen,
  ];
  const hash = [...seed].reduce((sum, char) => sum + char.charCodeAt(0), 0);
  return palette[hash % palette.length];
};

export const StyledWrapper = styled.div`
  position: relative;
`;

export const StyledFieldRow = styled.div<{ hasError?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid
    ${({ hasError, theme }) =>
      hasError ? theme.color.bittersweet : theme.color.porcelain};
  cursor: text;
  transition: border-color 0.15s ease;

  &:focus-within {
    border-bottom-color: ${({ hasError, theme }) =>
      hasError ? theme.color.bittersweet : theme.color.brightTurquoise};
  }
`;

export const StyledFieldLabel = styled.label`
  flex-shrink: 0;
  width: 28px;
  padding-top: 7px;
  font-size: 14px;
  color: ${({ theme }) => theme.color.baliHai};
  cursor: text;
`;

export const StyledChips = styled.div<{ expanded?: boolean }>`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  max-height: ${({ expanded }) => (expanded ? '196px' : 'none')};
  overflow-y: ${({ expanded }) => (expanded ? 'auto' : 'visible')};
`;

export const StyledChip = styled.span<{ external?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  height: 32px;
  padding: 0 4px 0 4px;
  border-radius: 999px;
  border: 1px solid
    ${({ external, theme }) =>
      external ? theme.color.botticelli : theme.color.porcelain};
  background: ${({ external, theme }) =>
    external ? theme.color.aquaSqueeze : theme.color.white};
  box-shadow: 0 1px 2px ${({ theme }) => `${theme.color.pickledBluewood}14`};
  font-size: 13px;
  color: ${({ theme }) => theme.color.shark};
  animation: chipIn 0.16s ease-out;

  @keyframes chipIn {
    from {
      opacity: 0;
      transform: scale(0.9);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
`;

export const StyledChipLabel = styled.span`
  min-width: 0;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media ${breakpoints.downSmPlus} {
    max-width: 160px;
  }
`;

export const StyledChipRemove = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: ${({ theme }) => theme.color.baliHai};
  cursor: pointer;
  transition: background 0.12s ease, color 0.12s ease;

  svg {
    width: 12px;
    height: 12px;
  }

  &:hover,
  &:focus-visible {
    outline: none;
    background: ${({ theme }) => theme.color.blackSqueeze};
    color: ${({ theme }) => theme.color.shark};
  }
`;

export const StyledMoreChip = styled.button`
  height: 32px;
  padding: 0 12px;
  border: 1px dashed ${({ theme }) => theme.color.botticelli};
  border-radius: 999px;
  background: transparent;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.color.BlueBayoux};
  cursor: pointer;

  &:hover {
    border-color: ${({ theme }) => theme.color.brightTurquoise};
    color: ${({ theme }) => theme.color.skobeloffGreen};
  }
`;

export const StyledInput = styled.input`
  flex: 1;
  min-width: 160px;
  height: 32px;
  padding: 0;
  border: none;
  outline: none;
  background: transparent;
  font-size: 14px;
  color: ${({ theme }) => theme.color.shark};

  &::placeholder {
    color: ${({ theme }) => theme.color.casper};
  }
`;

export const StyledAvatar = styled.span<{ seed: string; size: number }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: ${({ size }) => size}px;
  height: ${({ size }) => size}px;
  border-radius: 50%;
  overflow: hidden;
  background: ${({ seed, theme }) => getAvatarColor(seed, theme)};
  color: ${({ theme }) => theme.color.white};
  font-size: ${({ size }) => Math.round(size * 0.4)}px;
  font-weight: 700;
  line-height: 1;
`;

export const StyledAvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

export const StyledSuggestions = styled.ul`
  position: absolute;
  z-index: 30;
  top: calc(100% - 4px);
  left: 40px;
  width: min(420px, calc(100% - 40px));
  max-height: 300px;
  margin: 0;
  padding: 6px;
  overflow-y: auto;
  list-style: none;
  border-radius: 14px;
  border: 1px solid ${({ theme }) => theme.color.porcelain};
  background: ${({ theme }) => theme.color.white};
  box-shadow: 0 18px 40px -12px ${({ theme }) => `${theme.color.pickledBluewood}40`};
  animation: suggestionsIn 0.14s ease-out;

  @keyframes suggestionsIn {
    from {
      opacity: 0;
      transform: translateY(-4px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media ${breakpoints.downSmPlus} {
    left: 0;
    width: 100%;
  }
`;

export const StyledOption = styled.li<{ highlighted?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 10px;
  cursor: pointer;
  background: ${({ highlighted, theme }) =>
    highlighted ? theme.color.blackSqueeze : 'transparent'};
`;

export const StyledOptionText = styled.span`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;
  text-align: left;
`;

export const StyledOptionName = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.color.shark};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const StyledOptionEmail = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.color.baliHai};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const StyledOptionEmpty = styled.li`
  padding: 12px 10px;
  font-size: 13px;
  color: ${({ theme }) => theme.color.baliHai};
`;

export const StyledFieldError = styled.div`
  margin-top: 6px;
  font-size: 12px;
  color: ${({ theme }) => theme.color.bittersweet};
`;
